import { useNavigate } from "react-router-dom";
import { Home, LogOut } from "lucide-react";
import { Logo } from "./Brand";
import { useAuth } from "../lib/auth";
import { BackButton, Breadcrumbs } from "../lib/nav";

export default function ConsoleShell({ title, subtitle, nav, active, onNav, children, badges = {}, crumbs, fallback = "/dashboard" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-[#F4F4F5] lg:flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-[#27272A] bg-[#0A0A0C] fixed inset-y-0">
        <div className="p-5 border-b border-[#27272A]">
          <Logo size={38} showText={false} />
          <p className="font-display font-bold mt-3">RAV3.LABS</p>
          <p className="mono-label text-[#71717A]">{subtitle}</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <button key={n.key} data-testid={`console-nav-${n.key}`} onClick={() => onNav(n.key)}
              className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${active === n.key ? "bg-[#E8552A]/15 text-[#E8552A]" : "text-[#A1A1AA] hover:bg-[#1A1A22] hover:text-[#F4F4F5]"}`}>
              <n.icon size={17} /> <span className="flex-1 text-left">{n.label}</span>
              {badges[n.key] > 0 && <span className="mono-label bg-[#E8552A] text-[#0A0A0C] rounded-full px-1.5 text-[0.6rem]">{badges[n.key]}</span>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-[#27272A]">
          <div className="flex items-center gap-2 px-2 py-2">
            <span className="w-8 h-8 rounded-full bg-[#E8552A]/20 flex items-center justify-center font-display font-bold text-[#E8552A]">{user?.name?.[0]}</span>
            <div className="min-w-0"><p className="text-sm truncate">{user?.name}</p><p className="mono-label text-[#71717A] truncate">{user?.email}</p></div>
          </div>
          <div className="flex gap-2 mt-2">
            <button data-testid="console-site" onClick={() => navigate("/")} className="flex-1 flex items-center justify-center gap-1 border border-[#27272A] rounded-lg py-2 mono-label hover:border-[#E8552A]"><Home size={13} /> SITE</button>
            <button data-testid="console-logout" onClick={() => { logout(); navigate("/"); }} className="flex-1 flex items-center justify-center gap-1 border border-[#27272A] rounded-lg py-2 mono-label hover:border-[#E8552A]"><LogOut size={13} /> LOGOUT</button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 pb-20 lg:pb-0">
        {/* Mobile header */}
        <header className="lg:hidden glass-nav sticky top-0 z-40 flex items-center justify-between px-4 h-14">
          <Logo size={32} showText={false} />
          <span className="mono-label text-[#71717A]">{subtitle}</span>
          <button data-testid="console-logout-mobile" onClick={() => { logout(); navigate("/"); }} className="text-[#71717A]"><LogOut size={18} /></button>
        </header>
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="flex items-center gap-4 flex-wrap mb-2"><BackButton testid="console-back" fallback={fallback} />{crumbs && <Breadcrumbs items={crumbs} />}</div>
          <p className="mono-label text-[#71717A]">// {subtitle}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase mt-1 mb-6">{title}</h1>
          {children}
        </div>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass-nav border-t border-[#27272A] flex justify-around py-1.5">
        {nav.slice(0, 5).map((n) => (
          <button key={n.key} data-testid={`console-bottomnav-${n.key}`} onClick={() => onNav(n.key)}
            className={`flex flex-col items-center gap-0.5 px-2 py-1.5 relative ${active === n.key ? "text-[#E8552A]" : "text-[#71717A]"}`}>
            <n.icon size={19} />
            <span className="text-[0.58rem] font-mono uppercase tracking-wide">{n.label.split(" ")[0]}</span>
            {badges[n.key] > 0 && <span className="absolute top-0 right-1 w-2 h-2 rounded-full bg-[#E8552A]" />}
          </button>
        ))}
      </nav>
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, testid, onClick }) {
  return (
    <div data-testid={testid} onClick={onClick} className={`card-copper rounded-xl p-5 ${onClick ? "cursor-pointer" : ""}`}>
      <div className="flex items-center justify-between"><p className="mono-label text-[#71717A]">{label}</p>{Icon && <Icon size={16} className="text-[#71717A]" />}</div>
      <p className="font-display text-3xl font-extrabold mt-2">{value}</p>
    </div>
  );
}
